// @flow

import { KeyboardArrowDownOutlined, KeyboardArrowRightOutlined } from "@mui/icons-material";
import { FormControl, FormLabel, Typography, Collapse } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { DataGridPremium, GridToolbar } from "@mui/x-data-grid-premium";
import axios from "axios";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FormBoxTitle from "../../components/mui/app/formBoxTitle/formBoxTitle";
import PageContentWrapper from "../../components/mui/app/pageContentWrapper/pageContentWrapper";
import PageTitle from "../../components/mui/app/pageTitle/pageTitle";
import CustomAutocomplete from "../../components/mui/form/autocomplete/autocomplete";
import DateRange from "../../components/mui/form/dateRange/dateRange";
import CollapseBox from "../../components/mui/general/collapseBox/collapseBox";
import SkeletonLoaderWrapper from "../../components/mui/general/skeletonLoaderWrapper/skeletonLoaderWrapper";
import { CustomPagination } from "../../components/mui/grids/dataGridCustom/customPagination";
import CustomToolbar from "../../components/mui/grids/dataGridCustom/customToolbar/customToolbar";
import DataGridWrapper from "../../components/mui/grids/dataGridWrapper/dataGridWrapper";
import { API } from "../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../helpers/errorHandling";
import { buildDynamicColumns, buildDynamicColumnsFromFilters } from "../../helpers/grid/getDynamicColumns";
import { mapDateRange } from "../../helpers/helpers";
import { muiGridLocales } from "../../helpers/mui/muiLocales";
import {
    statistics_view_aging,
    statistics_view_assignee,
    statistics_view_completed_within_SLA,
    statistics_view_customer, statistics_view_fm
} from "../../helpers/rights/rights";
import { formControlDefaults } from "../../helpers/theme/defaultProps";
import useReportStaticData from "../../hooks/reports/useReportStaticData";
import useUserAction from "../../hooks/user/useUserAction";
import useUserData from "../../hooks/user/useUserData";
import { REPORTS } from "../../locales/pages/namespaces";
import PermissionHOC, { withPermissionHOC } from "../../modules/permission/components/permissionHOC/permissionHOC";
import CardChip from "../dashboard/partials/cardChip";
import TruncateDescription from "../dashboard/partials/truncateDescription";

type Props = {};

export const mapFilters = (filtersData) => {
    const {
        Customer,
        ItemCategs,
        Priority,
        SubmitDt,
        Assignee,
        Address,
        Type,
        FM,
        Kind
    } = filtersData

    const [SubmitDtFrom, SubmitDtTo] = mapDateRange(SubmitDt)

    const getIds = (arr) =>
        Array.isArray(arr) && arr.length > 0
            ? arr.map(i => i.id).join(',')
            : ''

    const filtersObject = {
        Customer: getIds(Customer),
        ItemCategs: getIds(ItemCategs),
        Priority: getIds(Priority),
        Assignee: getIds(Assignee),
        FM: getIds(FM),
        Address: getIds(Address),
        Type: getIds(Type),
        Kind: getIds(Kind),
        SubmitDtFrom: SubmitDtFrom || '',
        SubmitDtTo: SubmitDtTo || ''
    }

    return { fields: filtersObject }
}


const Aging = (props: Props) => {
    const { t } = useTranslation(REPORTS)
    const { dateFormat, token, isLoggedIn, language, thousandSeparator, decimalSeparator } = useUserData()
    const { userLogout } = useUserAction()
    const [loadingSearch, setLoadingSearch] = useState(false)
    const [rows, setRows] = useState([])
    const [dynamicColumns, setDynamicColumns] = useState([])
    const [dynamicColumns2, setDynamicColumns2] = useState([])
    const [filtersData, setFiltersData] = useState({
        Customer: [],
        ItemCategs: [],
        Priority: [],
        SubmitDt: [],
        Assignee: [],
        Address: [],
        Type: [],
        FM: [],
        Kind: []
    })
    const {
        loading,
        customers,
        types,
        categs,
        priorities,
        assignees,
        addresses,
        FM,
        kinds
    } = useReportStaticData()
    const [customerOptions, setCustomerOptions] = useState(customers)
    const [sitesData, setSitesData] = useState(addresses)
    const [customerAddresses,setCustomerAddresses]= useState([])
    const [assigneeData, setAssigneeData] = useState(assignees)
    const [fmData, setFmData] = useState(FM)
    const [addressInput, setAddressInput] = useState('');
    const [assigneeInput, setAssigneeInput] = useState('');


    useEffect(() => {
        setCustomerOptions(customers || []);
        setSitesData(addresses || [])
        setAssigneeData(assignees || [])
        setFmData(FM || [])
    }, [customers, addresses, assignees, FM]);

    useEffect(() => {
        if (types && types.length > 0) {
            const defaultType = types.find(t => t.isDefault === '1');
            setFiltersData(prev => ({
                ...prev,
                Type: defaultType ? [defaultType] : []
            }));
        }
    }, [types]);


    const handleSearch = (params, isClear = false) => {

        const { SubmitDtFrom, SubmitDtTo } = params.fields || {};
console.log('params fields',params.fields)
        if (!isClear && (!SubmitDtFrom || !SubmitDtTo)) {
            enqueueSnackbar(t('Η ημερομηνία είναι υποχρεωτική!'), {
                variant: 'warning',
                autoHideDuration: 3000,
            });
            return;
        }
        setLoadingSearch(true)
        axios({
            method: 'post',
            url: `${API}/en/custom/action`,
            data: {
                action_id: 'aging',
                fields: params.fields
            },
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        }).then(res => {
            console.log('res', res.data)
            const data = res.data.data || []
            setRows(data)
            const monthCols = buildDynamicColumns(data, {
                includeKeys: ['avg'],
                locale: language,
                thousandSeparator: thousandSeparator,
                decimalSeparator: decimalSeparator
            })

            const filterCols = buildDynamicColumnsFromFilters(filtersData, [
                {
                    field: 'Assignee',
                    label: 'Assignee',
                    descriptionField: 'Assignee',
                    filterKey: 'Assignee'
                },
                {
                    field: 'CustName',
                    label: 'Customer',
                    descriptionField: 'CustName',
                    filterKey: 'Customer'
                },
                {
                    field: 'Address',
                    label: 'Site',
                    descriptionField: 'Address',
                    filterKey: 'Address'
                },
                {
                    field: 'FM',
                    label: 'FM',
                    descriptionField: 'FM',
                    filterKey: 'FM'
                },
            ], t)

            setDynamicColumns([...filterCols])
            setDynamicColumns2([...monthCols])
            setLoadingSearch(false)

            {
                data.length === 0 && !isClear &&
                enqueueSnackbar(t('No data found!'), {
                    variant: 'warning',
                    autoHideDuration: 3000,
                })
            }
        }).catch((error) => {
            setLoadingSearch(false)
            enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                variant: 'error',
                autoHideDuration: 3000,
            })
            logoutUnauthorizedUser(error, isLoggedIn, userLogout)
        })
    }

    const columns = useMemo(() => [
        {
            field: 'Category',
            headerName: t('Item Category'),
            description: t('Item Category'),
            flex: 1,
            minWidth: 150,
            renderCell: ({ row }) => {
                return <TruncateDescription value={row.Category || '-'}/>
            },
        },
        {
            field: 'Priority',
            headerName: t('Priority'),
            flex: 1,
            minWidth: 120,
            renderCell: ({ row }) => row.Priority && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <CardChip
                        label={row.Priority}
                        color={row?.PriotiryColor}
                        borderColor={row?.PriotiryColor}
                    />
                </Box>
            )
        },
        ...dynamicColumns,
        {
            field: 'open_avg',
            headerName: t('AVG Open'),
            description: t('AVG Open'),
            flex: 1,
            minWidth: 150,
        },
        ...dynamicColumns2

    ], [t, dynamicColumns,dynamicColumns2])

    const onChangeCustomer = useCallback((newValue) => {
        const getIds = (arr) =>
            Array.isArray(arr) && arr.length > 0
                ? arr.map(i => i.id).join(',')
                : ''
        const customerIds = getIds(newValue)

        setLoadingSearch(true)
        axios({
            method: 'post',
            data: {
                CustId: customerIds,
                ReturnData: ['addresses', 'assignees', 'FM']
            },
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            url: `${API}/en/issues/issueCustData`
        }).then((res) => {
            setLoadingSearch(false)
            setSitesData(_.orderBy(res.data?.addresses?.map(i => ({
                id: i.id,
                Description: i.Site
            })), ['Description'], ['asc']))

            setCustomerAddresses(_.orderBy(res.data?.addresses?.map(i => ({
                id: i.id,
                Description: i.Site
            })), ['Description'], ['asc']))
            setAssigneeData(_.orderBy(res.data?.assignees?.map(i => ({
                id: i.id,
                Description: i.Description
            })), ['Description'], ['asc']))
            setFmData(_.orderBy(res.data?.FM?.map(i => ({
                id: i.id,
                Description: i.Description
            })), ['Description'], ['asc']))

        }).catch((error) => {
            setLoadingSearch(false)
            enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                variant: 'error',
                autoHideDuration: 3000,
            })
            logoutUnauthorizedUser(error, isLoggedIn, userLogout)
        })
    }, [t, isLoggedIn, userLogout])

    const searchCustomers = useCallback((evt) => {
        setLoadingSearch(true)
        axios({
            method: 'post',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            data: {
                query: evt.target.value,
            },
            url: `${API}/en/customers/search`
        }).then((response) => {
            setLoadingSearch(false)
            setCustomerOptions(response.data.data)
        }).catch((error) => {
            enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                variant: 'error',
                autoHideDuration: 3000,
            })
            setLoadingSearch(false)
            logoutUnauthorizedUser(error, isLoggedIn, userLogout)
        })
    }, [t, isLoggedIn, userLogout])

    const searchSites = useCallback((query='') => {
        const getIds = (arr) =>
            Array.isArray(arr) && arr.length > 0
                ? arr.map(i => i.id).join(',')
                : ''
        const customerIds = getIds(filtersData.Customer)
        console.log('customerIds',customerIds)

        setLoadingSearch(true)
        axios({
            method: 'post',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            data: {
                CustId:customerIds,
                query:query ,
                ...(customerIds && { CustId: customerIds }),
                Type: 'Site'
            },
            url: `${API}/en/customer/address/search`
        }).then((response) => {
            setSitesData(response.data.data)
            setLoadingSearch(false)
        }).catch((error) => {
            setLoadingSearch(false)
            enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                variant: 'error',
                autoHideDuration: 3000,
            })
            logoutUnauthorizedUser(error, isLoggedIn, userLogout)
        })
    }, [filtersData.Customer, t, isLoggedIn, userLogout])

    const searchAssignees = useCallback((evt) => {
        const getIds = (arr) =>
            Array.isArray(arr) && arr.length > 0
                ? arr.map(i => i.id).join(',')
                : ''
        const customerIds = getIds(filtersData.Customer)
        setLoadingSearch(true)
        axios({
            method: 'post',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            url: `${API}/en/custom/action`,
            data: {
                action_id: 'assignee-search',
                fields:{
                    CustId:customerIds,
                    query:evt.target.value
                },
            },
        }).then((response) => {
            setSitesData(response.data.data)
            setLoadingSearch(false)
        }).catch((error) => {
            setLoadingSearch(false)
            enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                variant: 'error',
                autoHideDuration: 3000,
            })
            logoutUnauthorizedUser(error, isLoggedIn, userLogout)
        })
    }, [filtersData.Customer, t, isLoggedIn, userLogout])

    return (
        <Box>
            <PageContentWrapper>
                <PageTitle title={t('Ageing')}/>
                <SkeletonLoaderWrapper loading={loading}>
                    <CollapseBox title={t('Filter Options')}>
                        <Grid container spacing={2} sx={{ mt: 1, mb: 1, alignItems: 'flex-end' }}>
                            <PermissionHOC requiredPermissions={[statistics_view_customer]}>
                                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 3 }}>
                                    <FormControl
                                        {...formControlDefaults}
                                    >
                                        <FormLabel>{t('Customer')}</FormLabel>
                                        <CustomAutocomplete
                                            identifier="id"
                                            name="Customer"
                                            onChange={(evt, newValue, reason) => {
                                                setFiltersData({
                                                    ...filtersData,
                                                    Customer: newValue
                                                })
                                                if (reason === 'clear') {
                                                    setCustomerOptions([...customers])
                                                    setSitesData([...addresses])
                                                } else {
                                                    onChangeCustomer(newValue)
                                                }
                                            }}
                                            value={filtersData.Customer}
                                            options={customerOptions}
                                            labelKey={'Description'}  //LoginName or AssigneeName
                                            placeholder={t('Select one or more..')}
                                            onInputChange={(evt) => searchCustomers(evt)}
                                            otherprops={{
                                                multiple: true,
                                                disableCloseOnSelect: true,
                                                limitTags: 1,
                                                getOptionKey: (option) => (option?.id)
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                            </PermissionHOC>
                            <Grid size={{ xs: 6, sm: 4, md: 3, lg: 3 }}>
                                <FormControl
                                    {...formControlDefaults}
                                >
                                    <FormLabel>{t('Site')}</FormLabel>
                                    <CustomAutocomplete
                                        identifier="id"
                                        name="Address"
                                        onChange={(evt, newValue, reason) => {
                                            setFiltersData({
                                                ...filtersData,
                                                Address: newValue
                                            })
                                            if (reason === 'clear' && !filtersData.Customer?.id) {
                                                setSitesData([...addresses])
                                                setAddressInput('');
                                            }
                                        }}
                                        value={filtersData.Address}
                                        options={sitesData}
                                        // onInputChange={(evt) => {
                                        // 	searchSites(evt)
                                        // }}
                                        labelKey={'Description'}  //LoginName or AssigneeName
                                        placeholder={t('Select one or more..')}
                                        otherprops={{
                                            multiple: true,
                                            disableCloseOnSelect: true,
                                            limitTags: 1,
                                            getOptionKey: (option) => (option?.id),
                                            inputValue:addressInput,
                                            onInputChange: (event, newValue) => {
                                                setAddressInput(newValue ?? '');
                                                searchSites(newValue);
                                            },
                                            onClose: (event, reason) => {
                                                if (['blur', 'escape', 'toggleInput'].includes(reason)) {
                                                    setSitesData(customerAddresses || []);
                                                    setAddressInput('');
                                                }
                                            },
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                                <FormControl {...formControlDefaults}>
                                    <FormLabel>{t('Submit Date')}</FormLabel>
                                    <DateRange
                                        dateFormat={dateFormat}
                                        value={filtersData.SubmitDt}
                                        onChange={(newValue) => {
                                            setFiltersData(prev => ({
                                                ...prev,
                                                SubmitDt: newValue
                                            }))
                                        }}
                                        onClear={() => (
                                            setFiltersData(prev => ({
                                                ...prev,
                                                SubmitDt: null
                                            }))
                                        )}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                                <FormControl
                                    {...formControlDefaults}
                                >
                                    <FormLabel>{t('Item Category')}</FormLabel>
                                    <CustomAutocomplete
                                        identifier="id"
                                        name="ItemCategs"
                                        onChange={(evt, newValue, reason) => {
                                            setFiltersData({
                                                ...filtersData,
                                                ItemCategs: newValue
                                            })
                                        }}
                                        value={filtersData.ItemCategs}
                                        options={categs}
                                        labelKey={'Description'}  //LoginName or AssigneeName
                                        placeholder={t('Select one or more..')}
                                        otherprops={{
                                            multiple: true,
                                            disableCloseOnSelect: true,
                                            limitTags: 1,
                                            getOptionKey: (option) => (option?.id)
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                                <FormControl
                                    {...formControlDefaults}
                                >
                                    <FormLabel>{t('Priority')}</FormLabel>
                                    <CustomAutocomplete
                                        identifier="id"
                                        name="Priority"
                                        onChange={(evt, newValue, reason) => {
                                            setFiltersData({
                                                ...filtersData,
                                                Priority: newValue
                                            })
                                        }}
                                        value={filtersData.Priority}
                                        options={priorities}
                                        labelKey={'Description'}  //LoginName or AssigneeName
                                        placeholder={t('Select one or more..')}
                                        otherprops={{
                                            multiple: true,
                                            disableCloseOnSelect: true,
                                            limitTags: 1,
                                            getOptionKey: (option) => (option?.id)
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                                <FormControl
                                    {...formControlDefaults}
                                >
                                    <FormLabel>{t('Type')}</FormLabel>
                                    <CustomAutocomplete
                                        identifier="id"
                                        name="Type"
                                        onChange={(evt, newValue, reason) => {
                                            setFiltersData({
                                                ...filtersData,
                                                Type: newValue
                                            })
                                        }}
                                        value={filtersData.Type}
                                        options={types}
                                        labelKey={'Description'}  //LoginName or AssigneeName
                                        placeholder={t('Select one or more..')}
                                        otherprops={{
                                            multiple: true,
                                            disableCloseOnSelect: true,
                                            limitTags: 1,
                                            getOptionKey: (option) => (option?.id)
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <PermissionHOC requiredPermissions={[statistics_view_assignee]}>
                                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                                    <FormControl
                                        {...formControlDefaults}
                                    >
                                        <FormLabel>{t('Assignee')}</FormLabel>
                                        <CustomAutocomplete
                                            identifier="id"
                                            name="Assignee"
                                            onChange={(evt, newValue, reason) => {
                                                setFiltersData({
                                                    ...filtersData,
                                                    Assignee: newValue
                                                })
                                                if (reason === 'clear' ) {
                                                    setAssigneeData([...assignees])
                                                }
                                            }}
                                            value={filtersData.Assignee}
                                            options={assigneeData}
                                            onInputChange={(evt)=>searchAssignees(evt)}
                                            labelKey={'Description'}  //LoginName or AssigneeName
                                            placeholder={t('Select one or more..')}
                                            otherprops={{
                                                multiple: true,
                                                disableCloseOnSelect: true,
                                                limitTags: 1,
                                                getOptionKey: (option) => (option?.id),
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                            </PermissionHOC>
                            <PermissionHOC requiredPermissions={[statistics_view_fm]}>
                                <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                                    <FormControl
                                        {...formControlDefaults}
                                    >
                                        <FormLabel>{t('FM')}</FormLabel>
                                        <CustomAutocomplete
                                            identifier="id"
                                            name="FM"
                                            onChange={(evt, newValue, reason) => {
                                                setFiltersData({
                                                    ...filtersData,
                                                    FM: newValue
                                                })
                                                if (reason === 'clear' ) {
                                                    setFmData([...FM])
                                                }
                                            }}
                                            value={filtersData.FM}
                                            options={fmData}
                                            labelKey={'Description'}
                                            placeholder={t('Select one or more..')}
                                            otherprops={{
                                                multiple: true,
                                                disableCloseOnSelect: true,
                                                limitTags: 1,
                                                getOptionKey: (option) => (option?.id)
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                            </PermissionHOC>
                            <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                                <FormControl
                                    {...formControlDefaults}
                                >
                                    <FormLabel>{t('Kind')}</FormLabel>
                                    <CustomAutocomplete
                                        identifier="id"
                                        name="Kind"
                                        onChange={(evt, newValue, reason) => {
                                            setFiltersData({
                                                ...filtersData,
                                                Kind: newValue
                                            })
                                        }}
                                        value={filtersData.Kind}
                                        options={kinds ?? []}
                                        labelKey={'Description'}
                                        placeholder={t('Select one or more..')}
                                        otherprops={{
                                            multiple: true,
                                            disableCloseOnSelect: true,
                                            limitTags: 1,
                                            getOptionKey: (option) => (option?.id)
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 6, sm: 4, md: 4, lg: 4 }} sx={{ p: 0.4 }}>
                                <Box display={'flex'} alignItems={'flex-end'} justifyContent={'center'}
                                     flexWrap={'wrap'} gap={1}>
                                    <Button
                                        loading={loadingSearch}
                                        onClick={() => {
                                            const params = {
                                                ...mapFilters(filtersData)
                                            }
                                            handleSearch(params, false)
                                        }}
                                        variant="outlined"
                                        color={'success'}
                                    >
                                        {t('Search')}
                                    </Button>
                                    <Button
                                        loading={loadingSearch}
                                        onClick={() => {
                                            const clearedFilters = {
                                                Customer: [],
                                                ItemCategs: [],
                                                Priority: [],
                                                SubmitDt: null,
                                                Assignee: [],
                                                Address: [],
                                                Type: [],
                                                FM: [],
                                                Kind: []
                                            }
                                            setFiltersData(clearedFilters)

                                            const params = mapFilters(clearedFilters)
                                            handleSearch(params, true)
                                        }}
                                        variant="outlined"
                                        color={'error'}
                                    >
                                        {t('Clear')}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </CollapseBox>
                </SkeletonLoaderWrapper>
                {rows.length > 0 &&
                    <DataGridWrapper sx={{ height: 700 }}>
                        <DataGridPremium
                            rows={rows}
                            columns={columns}
                            loading={loadingSearch}
                            getRowId={(row)=>row.id}
                            pagination
                            pageSize={100}
                            rowsPerPageOptions={[10, 20, 50, 100]}
                            initialState={{
                                density: 'compact'
                            }}
                            slots={{
                                toolbar: GridToolbar,
                                pagination: CustomPagination,
                            }}
                            disableSelectionOnClick
                            localeText={muiGridLocales(language)}
                        />

                    </DataGridWrapper>}
            </PageContentWrapper>
        </Box>
    );
};

export default withPermissionHOC(Aging, [statistics_view_aging])